import { allSettled } from "promise";
import {
  ChangeEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ItemProps,
  ListMenuProps,
} from "@campgladiator/cgui-core.molecules.list-menu";
import { fetchPrograms } from "@campgladiator/cg-common.api.programs";
import { fetchTrainerByName } from "@campgladiator/cg-common.api.trainers";
import {
  ProductSubType,
  ProductType,
  ProgramScheduleDTO,
  TrainerProgramApproval,
  TrainerServiceTrainer,
} from "@campgladiator/cg-common.types.types";
import { getDistinctItemsWithProperties } from "@campgladiator/cg-common.utility.objects";
import { debounce } from "lodash";
import { getProgramProductTypeAndSubType } from "./logic/getProgramProductTypeAndSubType";
import { getSearchBarIcon, getTrainerItems } from "./logic";
import { SelectedTrainer } from "./SelectTrainerInput";

type UseSelectTrainerProps = {
  approvedProgram: TrainerProgramApproval[];
  onClearTrainerSearch: () => void;
  onItemSelect: (id: ItemProps) => void;
  selectedProductType: ProductType;
  selectedProductSubType: ProductSubType;
  selectedTrainer?: SelectedTrainer;
  setIsTrainerLoading: (isLoading: boolean) => void;
  setTrainerProgramSchedules: (programSchedules: ProgramScheduleDTO[]) => void;
};

const placeHolderText = "Search trainer";
const noRecordsFoundItem: ListMenuProps["items"] = [
  {
    type: "item",
    itemId: "0",
    text: "No results found",
    active: false,
    disablePointer: true,
  },
];
const isSearchingItem: ListMenuProps["items"] = [
  {
    type: "item",
    itemId: "0",
    text: "Searching...",
    active: false,
    disablePointer: true,
  },
];

const useSelectTrainer = ({
  approvedProgram,
  onClearTrainerSearch,
  onItemSelect,
  selectedProductSubType,
  selectedProductType,
  selectedTrainer,
  setIsTrainerLoading,
  setTrainerProgramSchedules,
}: UseSelectTrainerProps) => {
  const [userInput, setUserInput] = useState<string>("");
  const [filteredTrainers, setFilteredTrainers] = useState<
    ListMenuProps["items"]
  >([]);
  const [showList, setShowList] = useState<boolean>(false);
  const isFirstLoad = useRef<boolean>(true);

  const productDetailsInitialState = { type: "", subType: "" };

  const searchFieldRef = useRef<HTMLInputElement>(null);

  const useOuterClick = (
    ref: RefObject<HTMLElement>,
    callback: { (): void }
  ) => {
    const handleClick = (event: Event) => {
      if (!ref.current?.contains(event.target as Node)) {
        callback();
      }
    };
    useEffect(() => {
      window.addEventListener("mousedown", handleClick);

      return () => {
        window.removeEventListener("mousedown", handleClick);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
  };

  useOuterClick(searchFieldRef, () => {
    setShowList(false);
  });

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setShowList(true);
    setUserInput(event.currentTarget.value);
  };

  const resetSearch = useCallback(() => {
    setFilteredTrainers(isSearchingItem);
    setShowList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchTrainers = useCallback(async () => {
    setIsTrainerLoading(true);
    setFilteredTrainers(isSearchingItem);
    try {
      const trainersWithPrograms = [];
      const filteredTrainers = await fetchTrainerByName({
        trainerName: userInput,
      });

      const trainersPromises = approvedProgram
        .filter((ap) => !!ap)
        .map(async (programType) => {
          const { productType, productSubType } =
            getProgramProductTypeAndSubType[programType];

          return await allSettled(
            filteredTrainers.map(async (trainer) => {
              const programs = await fetchPrograms({
                urlParameters: `?trainerId=${trainer.id}&productType=${productType}&productSubType=${productSubType}`,
              });

              return {
                ...trainer,
                programProductTypeAndSubType: programs,
              };
            })
          );
        });

      const settledPromises = await Promise.all(
        trainersPromises.map(async (promise) => {
          return await allSettled(await promise);
        })
      );

      settledPromises.forEach((results) => {
        for (const result of results) {
          if (result.status === "fulfilled") {
            trainersWithPrograms.push(result.value);
          }
        }
      });

      const trainersWithProgramSchedules = trainersWithPrograms
        .filter((trainerWithProgram) => {
          const { programProductTypeAndSubType } = trainerWithProgram.value;
          return programProductTypeAndSubType?.programs?.length!;
        })
        .map((trainerWithProgram) => trainerWithProgram.value);

      const distinctTrainers = getDistinctItemsWithProperties(
        trainersWithProgramSchedules,
        "id"
      );

      const formattedTrainers = distinctTrainers
        .filter(({ approvedPrograms }: TrainerServiceTrainer) =>
          selectedProductType === "NUTRITION"
            ? approvedPrograms.nutritionApproved
            : (selectedProductSubType === "IN_PERSON" &&
                approvedPrograms.outdoorApproved) ||
              (selectedProductSubType === "ONLINE" &&
                (approvedPrograms.onlineApproved ||
                  approvedPrograms.outdoorApproved))
        )
        .map((trainer) => ({
          type: "item",
          text: `${trainer.firstName} ${trainer.lastName}`,
          itemId: trainer.id,
          avatar: {
            src: trainer.photo,
          },
          programSchedules: mountTrainerProgramSchedules(
            trainersWithProgramSchedules,
            trainer
          ),
        }));

      const noRecordsFound = formattedTrainers.length === 0;

      if (noRecordsFound) {
        setFilteredTrainers(noRecordsFoundItem);
        return;
      }

      const items = getTrainerItems({
        filteredPredictions: formattedTrainers as SelectedTrainer[],
        setFilteredTrainers,
        setUserInput,
        setShowList,
        handleOnOptionSelect,
      });

      setFilteredTrainers(items as ListMenuProps["items"]);

      const selectedOption = items?.find(
        (trainer) =>
          selectedTrainer && trainer?.itemId === selectedTrainer?.itemId
      ) as any;
      if (selectedOption)
        setTrainerProgramSchedules(
          selectedOption?.programSchedules as ProgramScheduleDTO[]
        );
    } catch (error) {
      setFilteredTrainers([]);
    } finally {
      setIsTrainerLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvedProgram, userInput]);

  const handleOnOptionSelect = async (itemId: ItemProps) => {
    setIsTrainerLoading(true);
    onItemSelect(itemId);
    setShowList(false);
  };

  const mountTrainerProgramSchedules = (
    trainersWithProgramSchedules: any[],
    trainer: any
  ) =>
    trainersWithProgramSchedules
      .filter((trainerWithProgram) => trainerWithProgram.id === trainer.id)
      .map((trainerWithProgram) => {
        return trainerWithProgram?.programProductTypeAndSubType?.programs[0]?.programSchedules?.map(
          (schedule) => {
            return {
              ...schedule,
              approvalRequired:
                trainerWithProgram?.programProductTypeAndSubType?.productDetails
                  ?.approvalRequired,
            };
          }
        );
      })
      .reduce((acc, val) => acc.concat(val), []);

  const searchWithDebounce = useMemo(
    () => debounce(searchTrainers, 500),
    [searchTrainers]
  );

  const handleOnCancelIconClick = useCallback(() => {
    setFilteredTrainers([]);
    setShowList(false);
    setUserInput("");
    onClearTrainerSearch();
  }, [onClearTrainerSearch]);

  useEffect(() => {
    if (userInput?.length > 2) {
      if (isFirstLoad.current) {
        searchTrainers();
        isFirstLoad.current = false;
      } else {
        searchWithDebounce();
      }
    } else {
      resetSearch();
    }
    return searchWithDebounce.cancel;
  }, [userInput, searchTrainers, searchWithDebounce, resetSearch]);

  useEffect(() => {
    if (selectedTrainer) setUserInput(selectedTrainer.text);
  }, [selectedTrainer?.text]);

  return {
    filteredTrainers,
    isSearchingItem,
    placeHolderText,
    searchFieldRef,
    showList,
    userInput,
    getSearchBarIcon,
    handleOnCancelIconClick,
    handleOnChange,
  };
};
export default useSelectTrainer;
